// First-match camera controls hint. A small floating chip in the top-right that
// lists every way to move the camera, so a new player who tries "left-click
// drag" or "WASD only" doesn't conclude that panning is broken. Dismissable;
// also auto-dismisses after AUTO_DISMISS_MS or when the player issues any pan
// (so it doesn't sit there once they've found a working control).
//
// Persists dismissal in localStorage so returning players don't see it twice.

import { injectHudThemeStyles } from './hudThemeStyles.js';

const ROOT_ID = 'camera-controls-hint';
const STYLE_ID = 'camera-controls-hint-styles';
const STORAGE_KEY = 'signalclash.camera_hint.seen';
const AUTO_DISMISS_MS = 25_000;

export class CameraControlsHint {
  private root: HTMLDivElement | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private dismissed = false;

  show(): void {
    if (this.dismissed) return;
    if (this.alreadySeen()) return;
    injectStyles();
    if (this.root === null) this.root = ensureRoot();
    this.root.innerHTML = `
      <div class="cch__card">
        <div class="cch__head">
          <span class="cch__title">Move the camera</span>
          <button class="cch__close" type="button" aria-label="Dismiss">×</button>
        </div>
        <ul class="cch__list">
          <li><b>WASD</b> or <b>Arrow keys</b></li>
          <li>Drag with <b>right-click</b> or <b>middle-click</b></li>
          <li>Push the cursor to the <b>screen edge</b></li>
          <li>Scroll wheel to zoom</li>
        </ul>
      </div>
    `;
    this.root.style.display = 'block';
    const closeBtn = this.root.querySelector<HTMLButtonElement>('.cch__close');
    closeBtn?.addEventListener('click', () => this.dismiss());
    this.timer = setTimeout(() => this.dismiss(), AUTO_DISMISS_MS);
  }

  // Called by ClientApp when it detects the player has moved the camera — once
  // they've found a working pan control, the hint has served its purpose and
  // should make way for the game.
  acknowledge(): void {
    if (this.root !== null && this.root.style.display !== 'none') {
      this.dismiss();
    }
  }

  dismiss(): void {
    this.dismissed = true;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.root !== null) {
      this.root.style.display = 'none';
    }
    this.markSeen();
  }

  private alreadySeen(): boolean {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  private markSeen(): void {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // localStorage unavailable; the hint will replay next session.
    }
  }
}

function ensureRoot(): HTMLDivElement {
  const existing = document.getElementById(ROOT_ID);
  if (existing instanceof HTMLDivElement) return existing;
  const div = document.createElement('div');
  div.id = ROOT_ID;
  document.body.appendChild(div);
  return div;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${ROOT_ID} {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 34;
  pointer-events: none;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  display: none;
  animation: cch-in 320ms ease-out;
}
.cch__card {
  pointer-events: auto;
  width: 260px;
  padding: 10px 14px 12px;
  border-radius: 8px;
  background: rgba(12, 14, 18, 0.94);
  border: 1px solid rgba(74, 150, 255, 0.55);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.55), 0 0 12px rgba(0, 212, 255, 0.18);
  color: #d4dae2;
  font-size: 12px;
  line-height: 1.45;
}
.cch__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}
.cch__title {
  color: #a8d2ff;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.cch__close {
  background: transparent;
  border: none;
  color: #8d97a7;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
}
.cch__close:hover { color: #d4dae2; }
.cch__list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.cch__list li {
  padding: 2px 0;
  color: #b5bccb;
}
.cch__list b {
  color: #e2e8f1;
  font-weight: 600;
}
@keyframes cch-in {
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
}
  `;
  document.head.appendChild(style);
  injectHudThemeStyles();
}
